import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { LoaderService } from '@app/core/loader.service';
import { CartStatus } from '@app/models/cart';
import { Item } from '@app/models/item';
import { Navigation, Route } from '@app/models/navigation';
import { CurrencyPipe, NumberToConvertExtended } from '@app/shared/currency.pipe';
import { SetNavigation } from '@app/store/actions/cart.action';
import {
  Checkout3DS,
  DenyIban,
  PaymentCheckout,
  PaymentRedirect,
  SetAddingNewCard,
} from '@app/store/actions/payment.action';
import { CartState } from '@app/store/state/cart.state';
import { translate } from '@ngneat/transloco';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { NavigationService } from '../core/navigation/navigation.service';
import { PaymentState } from '../store/state/payment.state';
import { urlIncludes, WHITE_BACKGROUND_URLS } from '@app/shared/helpers/url';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RedirectDialogComponent } from '@app/order/payment/dialogs/redirect/redirect.component';
import { StepNavigationService } from '@app/shared/services/step-navigation/step-navigation.service';
import { isNullish } from '@infomaniak/ngx-helpers';

export interface Step {
  hidden: boolean;
  title: string;
  rank: number;
}

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
  animations: [
    trigger('enterAnimationFooter', [
      transition(':enter', [style({ marginBottom: '-85px' }), animate('1s ease-out', style({ marginBottom: '0px' }))]),
      transition(':leave', [style({ marginBottom: '0px' }), animate('1s ease-out', style({ marginBottom: '-85px' }))]),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class OrderComponent implements OnInit, OnDestroy {
  /*
   * OrderComponent load the step of the current order
   */
  @Select(CartState.navigation)
  navigation$: Observable<Navigation>;

  @Select(CartState.promo)
  promo$: Observable<any[]>;

  @Select(CartState.priceBeforeDiscount)
  realPrice$: Observable<NumberToConvertExtended>;

  @Select(CartState.price)
  price$: Observable<NumberToConvertExtended>;

  @Select(PaymentState.target)
  target$: Observable<any>;

  @Select(CartState.status)
  status$: Observable<CartStatus>;

  @Select(PaymentState.cardUri)
  cardURI$: Observable<string>;

  @Select(CartState.items)
  item$: Observable<Item[]>;

  @Select(PaymentState.postfinance)
  postfinance$: Observable<any>;

  item: Item = null;
  trialPeriod = '90';
  currencyPipe = new CurrencyPipe();

  activeRoute: number;
  displayedActiveRoute: number;
  activeRouteConfiguration: any;
  user: boolean;
  isLoading: boolean;
  isItemLoading = false;
  routes: Route[];
  currentItemId: number = null;
  steps: Step[];
  displayedSteps: Step[];
  loading = false;
  isLastStep: boolean;
  pending = false;
  noTarget: boolean;
  target = null;
  promoList: string;

  isNavigating = false;
  isStudent = false;

  isConnected = window['CURRENT_ACCOUNT'] !== null;
  countryHasVAT = true;
  studentServices: string = null;

  routeData;
  callback;
  isMobile = false;
  isTablet = false; // Only used by switch vat
  orderState = window['CURRENT_STATE'].includes('order');

  topHidden = true;
  private onDestroy$ = new Subject<boolean>();

  constructor(
    private store: Store,
    private navigationService: NavigationService,
    private actions$: Actions,
    @Inject(DOCUMENT) private document: Document,
    private loader: LoaderService,
    private router: Router,
    public breakpointObserver: BreakpointObserver,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private stepNavigationService: StepNavigationService<unknown>
  ) {
    this.breakpointObserver
      .observe(['(max-width: 767px)'])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
    this.breakpointObserver
      .observe(['(max-width: 1200px)'])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.isTablet = result.matches;
      });
    this.actions$
      .pipe(ofActionDispatched(DenyIban))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.loader.set(false);
      });
    this.actions$
      .pipe(ofActionDispatched(Checkout3DS))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((action) => {
        this.loader.set(action.payload);
      });
    this.actions$
      .pipe(ofActionDispatched(PaymentRedirect))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((action) => {
        this.loader.set(false);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.panelClass = 'dialog--medium';

        this.dialog.open(RedirectDialogComponent, dialogConfig);
        if (action.url) {
          setTimeout(() => {
            window.location.assign(action.url);
          }, 4000);
        }
      });
    this.actions$
      .pipe(ofActionDispatched(SetAddingNewCard))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((action) => {
        this.loader.item.set(action.payload);
      });
    this.target$.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
      this.noTarget = !x;
      this.target = x;
    });

    this.promo$.pipe(takeUntil(this.onDestroy$)).subscribe((prom) => {
      if (prom) {
        this.promoList = prom.map((p) => p.type).join(', ');
      }
    });
    this.actions$.pipe(ofActionDispatched(SetNavigation), takeUntil(this.onDestroy$)).subscribe((navigation) => {
      if (navigation.payload.target < 1) {
        this.stepNavigationService.resetSavedStep();
      }
    });

    this.navigationService
      .isNavigating()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isNavigating) => {
        this.isNavigating = isNavigating;
      });

    this.loader.isLoading$.pipe(takeUntil(this.onDestroy$)).subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loader.item.isLoading$.pipe(takeUntil(this.onDestroy$)).subscribe((loading) => {
      this.pending = loading;
      this.isItemLoading = loading;
    });

    // we need to subscribe to navigation to be able to set the navigationbar state correctly
    this.navigation$.pipe(takeUntil(this.onDestroy$)).subscribe((nav) => {
      if (nav && nav.workflow) {
        this.steps = nav.workflow
          .map((x) => {
            return {
              title: x.label,
              rank: x.order,
              hidden: x.hidden,
            } as Step;
          })
          .sort((a, b) => a.rank - b.rank);
        this.displayedSteps = this.steps
          .filter((x) => !x.hidden)
          .map((step: Step, index: number) => {
            const copy = cloneDeep(step);
            copy.rank = index + 1;
            return copy;
          });
        this.activeRoute = nav.current_step;
        this.displayedActiveRoute = nav.current_step;
        for (let i = 0; i < this.activeRoute; i++) {
          if (this.steps[i].hidden) {
            this.displayedActiveRoute--;
          }
        }
        const active_step = nav.workflow.find((x) => x.order === this.activeRoute);
        this.orderState = this.orderState && !['login', 'change_account'].includes(active_step.name);
        this.activeRouteConfiguration = active_step.configuration || [];
        this.currentItemId = nav.workflow.find((x) => x.name === 'select')
          ? nav.workflow.find((x) => x.name === 'select')['cart_item_id']
          : null;
        // if it's LastStep, we want to trigger the Next button to Pay Button
        this.isLastStep = this.activeRoute === Math.max(...this.steps.map((step) => step.rank));
      }
    });
    this.item$.pipe(takeUntil(this.onDestroy$)).subscribe((items) => {
      if (items) {
        this.item = items[0];
        try {
          this.trialPeriod = items[0].configuration.setup.trial_period;
        } catch (e) {
          this.trialPeriod = '90';
        }
        if (items.some(item => !isNullish(item.validation.errors?.['restricted_account']))) {
          const navigation = this.store.selectSnapshot(CartState.getCart).navigation;
          if (navigation.current_step !== navigation.workflow[0].order) {
            this.store.dispatch(new SetNavigation({current_step: null, goTo: navigation.workflow[0].name, target: null}));
          }
        }
        const specialOfferAccessCodeError: string[] = items.find((item) => item?.validation?.errors?.['special_offer_access_code'])?.validation.errors['special_offer_access_code'];
        if (specialOfferAccessCodeError?.length === 1) {
          switch (specialOfferAccessCodeError[0]) {
            case 'validation.ksuite_net_plus_leman':
              this.router.navigateByUrl('/error/ksuite-net-plus-expired');
              break;
            default:
              this.router.navigateByUrl('/error');
          }
        };

        this.studentServices = this.getStudentTooltipText(items);
      }
    });
    this.cardURI$.pipe(takeUntil(this.onDestroy$)).subscribe((url) => {
      if (url) {
        this.isLoading = false;
        this.pending = false;
      }
    });

    this.postfinance$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
      if (data && data.fields) {
        this.isLoading = false;
        this.pending = false;
      }
    });

    this.router.events
      .pipe(takeUntil(this.onDestroy$))
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute.snapshot),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .subscribe((route: ActivatedRouteSnapshot) => {
        this.routeData = route.data;
      });
  }

  get hasWhiteBackground(): boolean {
    return urlIncludes(WHITE_BACKGROUND_URLS);
  }

  async ngOnInit() {}

  // try to navigate to another place, if it's impossible, no error is raised => console logged
  tryNavigate(rank: number, target?: number) {
    this.store.dispatch(
      new SetNavigation({
        current_step: this.steps.find((x) => x.rank === rank).title,
        target: target || 0,
      })
    );
  }

  // the payment facility is set in the payment component, we only call this method to trigger the checkout
  checkout() {
    this.store.dispatch(new PaymentCheckout());
  }

  // try to call the callback method passed from children component data
  callChildrenCallback() {
    if (this.routeData && this.routeData.callback) {
      this.routeData
        .callback()
        .pipe(take(1))
        .subscribe((result) => {
          if (result === true) {
            this.tryNavigate(this.activeRoute, 1);
          }
        });
    }
  }

  callChildrenReturnCallback() {
    if (this.routeData && this.routeData.returnCallBack) {
      this.routeData
        .returnCallBack()
        .pipe(take(1))
        .subscribe((result) => {
          if (result === true) {
            this.tryNavigate(this.activeRoute, -1);
          }
        });
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  getPrice(period: any) {
    const myPeriod = (this.item as any).configuration.prices.dynamic.period.find((x) => x.unit === period);
    return { amountVAT: myPeriod.prices.fixed.incl_vat, amountNoVAT: myPeriod.prices.fixed.excl_vat };
  }

  getTextAfter() {
    let period: string;
    let prefix = translate('par');

    switch (this.item.attributes['period']) {
      case 1:
        period = translate('month');
        break;
      case 3:
        period = translate('trimestre');
        break;
      case 12:
        period = translate('an');
        break;
      default:
        prefix = `${translate('tous les')} ${this.item.attributes['period'] / 12}`;
        period = translate('ans');
        break;
    }

    return `${prefix} ${period}`;
  }

  private getStudentTooltipText(items: Item[]) {
    const studentServices = Array.from(new Set(this.hasStudentDiscount(items, [])).values());

    if (studentServices.length == 0) {
      return '';
    } else {
      this.isStudent = true;
    }

    if (studentServices.length == 1) {
      return translate("Vous disposez d'un $service$ gratuit avec votre statut étudiant", {
        service: translate(studentServices[0]),
      });
    }

    let text = translate('Vous disposez de produits gratuits avec votre statut étudiant.') + ' ' + '(';
    studentServices.forEach((service) => {
      text += translate(service) + ', ';
    });
    return text.replace(/. $/, ')');
  }

  private hasStudentDiscount(items: Item[], studentServices: any[]) {
    items.forEach((item) => {
      if (
        item.discounts.some((discount) => {
          return discount.type == 'system_student_cart_condition';
        })
      ) {
        studentServices.push(item.setup.type);
      }
      if (item.sub_items) {
        studentServices = studentServices.concat(this.hasStudentDiscount(item.sub_items, studentServices));
      }
    });

    return studentServices;
  }
}
